import React, { useState, useEffect } from 'react';
import { Stethoscope, Calendar, Video, LogOut, User, Clock, Phone, FileText, Upload, Download, History, ChevronDown, ChevronUp, X, Key, Settings, CheckCircle, MessageCircle, Eye, EyeOff, Camera, ArrowLeft, CalendarDays, Edit3, PenLine } from 'lucide-react';
import VideoConsultation from './VideoConsultation';
import ConsultationNotes from './ConsultationNotes';
import IncomingCallModal from './IncomingCallModal';
import useIncomingCall from './useIncomingCall';
import DoctorScheduleManager from './DoctorScheduleManager';
import { getStatusLabel, canDoctorStart, canDoctorComplete } from './utils/appointmentStatus';
import { compressImage } from './utils/imageCompression';
import { formatDate } from './utils/formatDate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('doctor_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

function DoctorPortal({ onLogout }) {
  const [doctor, setDoctor] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [consultationHistory, setConsultationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeConsultation, setActiveConsultation] = useState(null);
  const [activeTab, setActiveTab] = useState('appointments'); // 'appointments', 'history', 'settings'
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesAppointment, setNotesAppointment] = useState(null);
  const [unreadDocs, setUnreadDocs] = useState({ total: 0, byPatient: {} });
  const [generatedNoteData, setGeneratedNoteData] = useState(null);
  
  // History filters state
  const [historySearch, setHistorySearch] = useState('');
  const [historyPeriod, setHistoryPeriod] = useState('all'); // 'today', 'week', 'month', 'all'
  const [historyType, setHistoryType] = useState('all'); // 'teleconsultation', 'presentiel', 'all'
  const [historySortOrder, setHistorySortOrder] = useState('recent'); // 'recent', 'oldest'

  // Get doctor token for incoming call checks
  const doctorToken = localStorage.getItem('doctor_token');
  
  // Hook for incoming call notifications (poll every 3 seconds when not in active consultation)
  const { incomingCall, acceptCall, rejectCall } = useIncomingCall(
    !activeConsultation, // Don't poll when already in a consultation
    doctorToken,
    3000
  );

  // WhatsApp reminder state
  const [whatsappSending, setWhatsappSending] = useState({});
  const [whatsappResult, setWhatsappResult] = useState({});

  // Send WhatsApp reminder to patient
  const sendWhatsAppReminder = async (appointmentId) => {
    setWhatsappSending(prev => ({ ...prev, [appointmentId]: true }));
    setWhatsappResult(prev => ({ ...prev, [appointmentId]: null }));
    
    try {
      const response = await fetch(`${API_URL}/api/whatsapp/send-reminder/${appointmentId}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.status === 'sent') {
        setWhatsappResult(prev => ({ ...prev, [appointmentId]: { success: true, message: 'Rappel WhatsApp envoyé !' } }));
      } else {
        setWhatsappResult(prev => ({ ...prev, [appointmentId]: { success: false, message: data.error || 'Échec de l\'envoi' } }));
      }
    } catch (error) {
      setWhatsappResult(prev => ({ ...prev, [appointmentId]: { success: false, message: 'Erreur de connexion' } }));
    } finally {
      setWhatsappSending(prev => ({ ...prev, [appointmentId]: false }));
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setWhatsappResult(prev => ({ ...prev, [appointmentId]: null }));
      }, 5000);
    }
  };

  // Handle accepting incoming call from patient
  const handleAcceptIncomingCall = async () => {
    const call = await acceptCall();
    if (call) {
      // Find the appointment from our list or create a minimal object
      const apt = appointments.find(a => a.appointment_id === call.appointment_id) || {
        appointment_id: call.appointment_id,
        patient_name: call.caller_name
      };
      setActiveConsultation(apt);
    }
  };

  useEffect(() => {
    const storedDoctor = localStorage.getItem('doctor_data');
    if (storedDoctor) {
      setDoctor(JSON.parse(storedDoctor));
    }
    fetchAppointments();
    fetchConsultationHistory();
    fetchUnreadDocs();
  }, []);

  const fetchUnreadDocs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/doctor/documents/unread-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('doctor_token')}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUnreadDocs({ total: data.total || 0, byPatient: data.unread_by_patient || {} });
    } catch (e) { /* silent */ }
  };

  const fetchAppointments = async () => {
    const storedDoctor = localStorage.getItem('doctor_data');
    if (!storedDoctor) return;
    
    const doctorData = JSON.parse(storedDoctor);
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/appointments?doctor_id=${doctorData.doctor_id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed');
        onLogout();
        return;
      }
      
      const data = await response.json();
      // Sort appointments: most recent first
      const sortedAppointments = (data.appointments || []).sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultationHistory = async () => {
    const storedDoctor = localStorage.getItem('doctor_data');
    if (!storedDoctor) return;
    
    const doctorData = JSON.parse(storedDoctor);
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/consultation-history?doctor_id=${doctorData.doctor_id}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401 || response.status === 403) {
        return;
      }
      
      const data = await response.json();
      setConsultationHistory(data.consultations || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const viewPatientRecord = async (patient, userId) => {
    setSelectedPatient({ ...patient, user_id: userId });
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/patient/${userId}/documents?doctor_id=${doctor.doctor_id}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setPatientDocuments(data.documents || []);
      setShowPatientModal(true);
    } catch (error) {
      console.error('Error fetching patient documents:', error);
    } finally {
      // Refresh the global unread badge whether the docs fetch succeeded or not
      fetchUnreadDocs();
    }
  };

  const startConsultation = async (appointment) => {
    // Notify the patient about the incoming call
    try {
      await fetch(`${API_URL}/api/video/call/initiate`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ appointment_id: appointment.appointment_id })
      });
    } catch (e) {
      console.warn('Could not notify patient:', e);
    }
    
    // Mark consultation as started
    fetch(`${API_URL}/api/video/room/start/${appointment.appointment_id}`, { 
      method: 'POST',
      headers: getAuthHeaders()
    });
    setActiveConsultation(appointment);
  };

  const completeAppointment = async (appointmentId) => {
    if (!window.confirm('Confirmer la fin de cette consultation ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/consultation/${appointmentId}/end?doctor_id=${doctor.doctor_id}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        alert('Consultation terminée');
        fetchAppointments();
        fetchConsultationHistory();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur');
      }
    } catch (error) {
      alert('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('doctor_token');
    localStorage.removeItem('doctor_data');
    onLogout();
  };

  // Open consultation notes modal
  const openNotesModal = (appointment, prefilledData = null) => {
    setNotesAppointment(appointment);
    setGeneratedNoteData(prefilledData);
    setShowNotesModal(true);
  };

  // Handle note generated from video consultation
  const handleNoteGenerated = (noteData) => {
    console.log('Note generated from consultation:', noteData);
    setGeneratedNoteData(noteData);
    // Modal will open automatically when consultation ends
  };

  // Handle consultation end
  const handleConsultationEnd = () => {
    const apt = activeConsultation;
    setActiveConsultation(null);
    
    // If we have generated note data, open the notes modal for validation
    if (generatedNoteData) {
      setTimeout(() => {
        openNotesModal(apt, generatedNoteData);
        setGeneratedNoteData(null);
      }, 500);
    }
    
    fetchAppointments();
    fetchConsultationHistory();
  };

  // Handle note saved
  const handleNoteSaved = (data) => {
    console.log('Note saved:', data);
    // Refresh appointments to show note indicator
    fetchAppointments();
    fetchConsultationHistory();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}min ${secs}s`;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/change-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          doctor_id: doctor.doctor_id,
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPasswordSuccess('Mot de passe modifié avec succès');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        setShowChangePassword(false);
      } else {
        setPasswordError(data.detail || 'Erreur lors du changement');
      }
    } catch (err) {
      setPasswordError('Erreur de connexion');
    }
  };

  // Filter and compute history statistics
  const getFilteredHistory = () => {
    let filtered = [...consultationHistory];
    
    // Search filter (by patient name)
    if (historySearch.trim()) {
      const searchLower = historySearch.toLowerCase();
      filtered = filtered.filter(apt => {
        const patientName = `${apt.patient?.first_name || ''} ${apt.patient?.last_name || ''}`.toLowerCase();
        return patientName.includes(searchLower);
      });
    }
    
    // Period filter
    if (historyPeriod !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        
        switch (historyPeriod) {
          case 'today':
            return aptDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return aptDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return aptDate >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    // Type filter
    if (historyType !== 'all') {
      filtered = filtered.filter(apt => {
        const isTeleconsultation = apt.consultation_history?.type === 'teleconsultation' || 
                                    apt.service?.service_id === 'teleconsultation' ||
                                    (apt.service?.name || '').toLowerCase().includes('téléconsult');
        return historyType === 'teleconsultation' ? isTeleconsultation : !isTeleconsultation;
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      const dateA = new Date(`${a.appointment_date} ${a.appointment_time}`);
      const dateB = new Date(`${b.appointment_date} ${b.appointment_time}`);
      return historySortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  const filteredHistory = getFilteredHistory();
  
  // Compute statistics
  const isTeleApt = (apt) =>
    apt.consultation_history?.type === 'teleconsultation' ||
    apt.service?.service_id === 'teleconsultation' ||
    (apt.service?.name || '').toLowerCase().includes('téléconsult');

  const historyStats = {
    total: consultationHistory.length,
    teleconsultations: consultationHistory.filter(apt => isTeleApt(apt)).length,
    avgDuration: (() => {
      const teleconsults = consultationHistory.filter(apt => apt.consultation_history?.duration_seconds);
      if (teleconsults.length === 0) return 0;
      const total = teleconsults.reduce((sum, apt) => sum + (apt.consultation_history?.duration_seconds || 0), 0);
      return Math.round(total / teleconsults.length);
    })(),
    thisMonth: consultationHistory.filter(apt => {
      const now = new Date();
      const aptDate = new Date(apt.appointment_date);
      return aptDate.getMonth() === now.getMonth() && aptDate.getFullYear() === now.getFullYear();
    }).length
  };

  if (activeConsultation) {
    return (
      <VideoConsultation
        appointmentId={activeConsultation.appointment_id}
        userId={doctor?.doctor_id}
        userName={doctor?.name}
        userRole="doctor"
        onEnd={handleConsultationEnd}
        onNoteGenerated={handleNoteGenerated}
      />
    );
  }

  return (
    <div className="doctor-portal">
      {/* Incoming call modal */}
      {incomingCall && (
        <IncomingCallModal
          call={incomingCall}
          onAccept={handleAcceptIncomingCall}
          onReject={rejectCall}
        />
      )}
      {/* Header */}
      <header className="doctor-header">
        <div className="header-content">
          <div className="header-logo">
            <a
              href="/"
              data-testid="doctor-back-to-site"
              title="Retour au site officiel"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'rgba(255,255,255,0.85)', textDecoration: 'none', fontSize: '0.85rem' }}
            >
              <ArrowLeft size={16} /> Site officiel
            </a>
            <Stethoscope size={28} />
            <span>Portail Médecin</span>
          </div>
          <div className="header-user">
            <span>{doctor?.name}</span>
            <button onClick={() => setActiveTab('settings')} className="settings-btn" title="Paramètres">
              <Settings size={18} />
            </button>
            <button onClick={handleLogout} className="logout-btn">
              <LogOut size={18} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="doctor-tabs">
        <button 
          className={`tab-btn ${activeTab === 'appointments' ? 'active' : ''}`}
          onClick={() => setActiveTab('appointments')}
        >
          <Calendar size={18} /> Consultations à venir
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <History size={18} /> Historique
        </button>
        <button 
          className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveTab('schedule')}
          data-testid="tab-schedule-btn"
        >
          <CalendarDays size={18} /> Mes créneaux
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={18} /> Paramètres
        </button>
      </div>

      {/* Main Content */}
      <main className="doctor-main">
        <div className="doctor-container">

          {/* Schedule Tab */}
          {activeTab === 'schedule' && (
            <DoctorScheduleManager />
          )}
          
          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="settings-section">
              <h1>Paramètres du compte</h1>
              
              <div className="settings-card">
                <div className="settings-card-header">
                  <Key size={24} />
                  <div>
                    <h3>Changer le mot de passe</h3>
                    <p>Modifiez votre mot de passe pour sécuriser votre compte</p>
                  </div>
                </div>
                
                {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
                {passwordError && <div className="error-message">{passwordError}</div>}
                
                <form onSubmit={handleChangePassword} className="password-form">
                  <div className="form-group">
                    <label>Mot de passe actuel</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                      >
                        {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Nouveau mot de passe</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        placeholder="••••••••"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                      >
                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <small style={{ color: '#666', fontSize: '0.75rem' }}>Min 8 caractères, 1 majuscule, 1 chiffre</small>
                  </div>
                  <div className="form-group">
                    <label>Confirmer le nouveau mot de passe</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        placeholder="••••••••"
                        minLength={8}
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                      >
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className="btn-primary">
                    <Key size={18} /> Changer le mot de passe
                  </button>
                </form>
              </div>
              
              <div className="settings-info">
                <h3>Informations du compte</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Nom</label>
                    <p>{doctor?.name}</p>
                  </div>
                  <div className="info-item">
                    <label>Spécialité</label>
                    <p>{doctor?.specialty}</p>
                  </div>
                  <div className="info-item">
                    <label>Email</label>
                    <p>{doctor?.email}</p>
                  </div>
                  <div className="info-item">
                    <label>Ville</label>
                    <p>{doctor?.city}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'appointments' && (
            <>
              <h1>Consultations à venir</h1>

              {unreadDocs.total > 0 && (
                <div
                  data-testid="unread-docs-banner"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 16px', marginBottom: 16,
                    background: 'linear-gradient(90deg, #fff7ed 0%, #ffedd5 100%)',
                    borderLeft: '4px solid #f59e0b',
                    borderRadius: 8, color: '#7c2d12', fontWeight: 500,
                  }}
                >
                  <FileText size={20} />
                  <span>
                    <strong>{unreadDocs.total}</strong> nouveau{unreadDocs.total > 1 ? 'x' : ''} document{unreadDocs.total > 1 ? 's' : ''} patient{unreadDocs.total > 1 ? 's' : ''} à consulter
                  </span>
                </div>
              )}

              {loading ? (
                <div className="loading">Chargement...</div>
              ) : appointments.length === 0 ? (
                <div className="empty-state">
                  <Calendar size={48} />
                  <p>Aucune consultation programmée</p>
                </div>
              ) : (
                <div className="appointments-grid">
                  {appointments.map((apt) => (
                    <div key={apt.appointment_id} className="appointment-card-doctor">
                      <div className="card-header">
                        <span className={`status-badge status-${apt.status}`}>
                          {getStatusLabel(apt.status)}
                        </span>
                        {apt.service?.name?.toLowerCase().includes('téléconsultation') && (
                          <span className="teleconsult-badge">
                            <Video size={14} /> Vidéo
                          </span>
                        )}
                      </div>
                      
                      <div className="card-patient">
                        <User size={20} />
                        <div>
                          <strong>
                            {apt.patient?.first_name} {apt.patient?.last_name}
                            {unreadDocs.byPatient[apt.user_id] > 0 && (
                              <span
                                data-testid={`patient-unread-${apt.user_id}`}
                                title={`${unreadDocs.byPatient[apt.user_id]} nouveau(x) document(s)`}
                                style={{
                                  marginLeft: 8, background: '#dc2626', color: '#fff',
                                  fontSize: 11, fontWeight: 700,
                                  padding: '2px 8px', borderRadius: 12,
                                  verticalAlign: 'middle',
                                }}
                              >
                                {unreadDocs.byPatient[apt.user_id]} nouveau doc.
                              </span>
                            )}
                          </strong>
                          <span>{apt.patient?.phone_number}</span>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); viewPatientRecord(apt.patient, apt.user_id); }}
                          className="btn-view-record"
                          title="Ouvrir le dossier médical du patient"
                          data-testid={`view-record-${apt.appointment_id}`}
                        >
                          <FileText size={16} />
                          <span>Voir dossier</span>
                          {unreadDocs.byPatient[apt.user_id] > 0 && (
                            <span className="view-record-badge">{unreadDocs.byPatient[apt.user_id]}</span>
                          )}
                        </button>
                      </div>
                      
                      <div className="card-details">
                        <div><Calendar size={16} /> {formatDate(apt.appointment_date)}</div>
                        <div><Clock size={16} /> {apt.appointment_time}</div>
                        <div><Stethoscope size={16} /> {apt.service?.name}</div>
                      </div>
                      
                      <div className="card-actions">
                        {/* Bouton téléconsultation */}
                        {apt.service?.name?.toLowerCase().includes('téléconsultation') && 
                         (canDoctorStart(apt.status) || apt.status === 'in-progress') && (
                          <button 
                            onClick={() => startConsultation(apt)}
                            className="btn-start-consultation"
                          >
                            <Video size={18} />
                            {apt.status === 'in-progress' ? 'Rejoindre' : 'Démarrer'}
                          </button>
                        )}
                        
                        {/* Bouton terminer consultation (non-vidéo) */}
                        {!apt.service?.name?.toLowerCase().includes('téléconsultation') && 
                         canDoctorComplete(apt.status) && (
                          <button 
                            onClick={() => completeAppointment(apt.appointment_id)}
                            className="btn-complete"
                          >
                            <CheckCircle size={18} /> Terminer
                          </button>
                        )}

                        {/* Bouton note de consultation - visible pour consultations en cours ou confirmées */}
                        {(apt.status === 'in-progress' || apt.status === 'confirmed' || apt.status === 'completed') && (
                          <button 
                            onClick={() => openNotesModal(apt)}
                            className="btn-notes"
                            data-testid="open-notes-btn"
                          >
                            <FileText size={18} />
                            {apt.has_consultation_note ? 'Voir Note' : 'Créer Note'}
                          </button>
                        )}

                        {/* Bouton rappel WhatsApp */}
                        {(apt.status === 'pending' || apt.status === 'confirmed') && (
                          <button 
                            onClick={() => sendWhatsAppReminder(apt.appointment_id)}
                            className="btn-whatsapp"
                            disabled={whatsappSending[apt.appointment_id]}
                            title="Envoyer un rappel WhatsApp au patient"
                            style={{
                              background: whatsappResult[apt.appointment_id]?.success ? '#22c55e' : 
                                         whatsappResult[apt.appointment_id]?.success === false ? '#ef4444' : '#25D366',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              cursor: whatsappSending[apt.appointment_id] ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '0.85rem',
                              opacity: whatsappSending[apt.appointment_id] ? 0.7 : 1
                            }}
                          >
                            <MessageCircle size={16} />
                            {whatsappSending[apt.appointment_id] ? 'Envoi...' : 
                             whatsappResult[apt.appointment_id]?.success ? '✓ Envoyé' :
                             whatsappResult[apt.appointment_id]?.success === false ? 'Échec' : 'WhatsApp'}
                          </button>
                        )}
                      </div>

                      {/* WhatsApp result message */}
                      {whatsappResult[apt.appointment_id] && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          background: whatsappResult[apt.appointment_id].success ? '#dcfce7' : '#fee2e2',
                          color: whatsappResult[apt.appointment_id].success ? '#166534' : '#991b1b'
                        }}>
                          {whatsappResult[apt.appointment_id].message}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'history' && (
            <>
              <h1>Historique des Consultations</h1>
              
              {/* Statistics Cards */}
              <div className="history-stats" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
              }}>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{historyStats.total}</div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total consultations</div>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{historyStats.thisMonth}</div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Ce mois</div>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{historyStats.teleconsultations}</div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Téléconsultations</div>
                </div>
                <div className="stat-card" style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{formatDuration(historyStats.avgDuration)}</div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Durée moyenne</div>
                </div>
              </div>

              {/* Filters */}
              <div className="history-filters" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '20px',
                padding: '16px',
                background: '#f8fafc',
                borderRadius: '12px'
              }}>
                {/* Search */}
                <div style={{ flex: '1 1 200px' }}>
                  <input
                    type="text"
                    placeholder="Rechercher un patient..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '0.95rem'
                    }}
                    data-testid="history-search"
                  />
                </div>
                
                {/* Period Filter */}
                <select
                  value={historyPeriod}
                  onChange={(e) => setHistoryPeriod(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                  data-testid="history-period-filter"
                >
                  <option value="all">Toutes les périodes</option>
                  <option value="today">Aujourd'hui</option>
                  <option value="week">Cette semaine</option>
                  <option value="month">Ce mois</option>
                </select>
                
                {/* Type Filter */}
                <select
                  value={historyType}
                  onChange={(e) => setHistoryType(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                  data-testid="history-type-filter"
                >
                  <option value="all">Tous les types</option>
                  <option value="teleconsultation">Téléconsultations</option>
                  <option value="presentiel">Consultations présentielles</option>
                </select>
                
                {/* Sort */}
                <select
                  value={historySortOrder}
                  onChange={(e) => setHistorySortOrder(e.target.value)}
                  style={{
                    padding: '10px 14px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                  data-testid="history-sort"
                >
                  <option value="recent">Plus récent</option>
                  <option value="oldest">Plus ancien</option>
                </select>
              </div>

              {/* Results count */}
              <div style={{ 
                marginBottom: '16px', 
                color: '#64748b', 
                fontSize: '0.9rem' 
              }}>
                {filteredHistory.length} consultation{filteredHistory.length > 1 ? 's' : ''} trouvée{filteredHistory.length > 1 ? 's' : ''}
                {historySearch && ` pour "${historySearch}"`}
              </div>
              
              {filteredHistory.length === 0 ? (
                <div className="empty-state">
                  <History size={48} />
                  <p>{consultationHistory.length === 0 ? 'Aucune consultation terminée' : 'Aucun résultat pour ces filtres'}</p>
                  {consultationHistory.length > 0 && (
                    <button 
                      onClick={() => {
                        setHistorySearch('');
                        setHistoryPeriod('all');
                        setHistoryType('all');
                      }}
                      style={{
                        marginTop: '12px',
                        padding: '8px 16px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              ) : (
                <div className="history-list">
                  {filteredHistory.map((apt) => (
                    <div key={apt.appointment_id} className="history-item">
                      <div className="history-date">
                        <Calendar size={16} />
                        {formatDate(apt.appointment_date)} - {apt.appointment_time}
                      </div>
                      <div className="history-patient" onClick={() => viewPatientRecord(apt.patient, apt.user_id)} style={{cursor: 'pointer'}}>
                        <User size={18} />
                        <span>{apt.patient?.first_name} {apt.patient?.last_name}</span>
                        <FileText size={14} className="view-record-icon" />
                      </div>
                      <div className="history-service">
                        <Stethoscope size={16} />
                        {apt.service?.name}
                      </div>
                      {apt.consultation_history?.type === 'teleconsultation' && (
                        <div className="history-duration">
                          <Clock size={16} />
                          Durée: {formatDuration(apt.consultation_history?.duration_seconds)}
                        </div>
                      )}
                      <div className="history-actions">
                        <button 
                          onClick={() => openNotesModal(apt)}
                          className="btn-notes-small"
                        >
                          <FileText size={16} />
                          {apt.has_consultation_note ? 'Voir Note' : 'Créer Note'}
                        </button>
                      </div>
                      <span className={`status-badge ${isTeleApt(apt) ? 'status-teleconsult' : 'status-completed'}`}>
                        {isTeleApt(apt) ? 'Téléconsultation' : 'Présentiel'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Consultation Notes Modal */}
      {showNotesModal && notesAppointment && (
        <div 
          className="notes-modal-overlay" 
          onClick={() => setShowNotesModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              width: '100%', 
              maxWidth: '800px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
            }}
          >
            <ConsultationNotes
              appointmentId={notesAppointment.appointment_id}
              doctorToken={localStorage.getItem('doctor_token')}
              patientInfo={notesAppointment.patient ? {
                name: `${notesAppointment.patient.first_name || ''} ${notesAppointment.patient.last_name || ''}`.trim(),
                age: null,
                medical_history: notesAppointment.patient.medical_history || '',
                allergies: notesAppointment.patient.allergies || ''
              } : null}
              prefilledData={generatedNoteData}
              onNoteSaved={(data) => {
                handleNoteSaved(data);
                setGeneratedNoteData(null);
              }}
              onClose={() => {
                setShowNotesModal(false);
                setNotesAppointment(null);
                setGeneratedNoteData(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Patient Record Modal */}
      {showPatientModal && selectedPatient && (
        <PatientRecordModal
          patient={selectedPatient}
          documents={patientDocuments}
          doctorId={doctor?.doctor_id}
          onClose={() => {
            setShowPatientModal(false);
            setSelectedPatient(null);
          }}
          onRefresh={() => viewPatientRecord(selectedPatient, selectedPatient.user_id)}
        />
      )}
    </div>
  );
}

// Patient Record Modal Component
function PatientRecordModal({ patient, documents, doctorId, onClose, onRefresh }) {
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({
    document_type: 'consultation_report',
    document_name: '',
    notes: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [inputMode, setInputMode] = useState('file'); // 'file' | 'camera' | 'manual'
  const [manualContent, setManualContent] = useState('');

  const documentTypes = {
    'prescription': 'Ordonnance',
    'exam_request': 'Bon de Demande d\'Examens',
    'lab_result': 'Résultat d\'Analyse',
    'xray': 'Radiographie',
    'consultation_report': 'Rapport de Consultation',
    'certificate': 'Certificat Médical',
    'other': 'Autre'
  };

  const resetUploadForm = () => {
    setShowUpload(false);
    setUploadFile(null);
    setManualContent('');
    setInputMode('file');
    setUploadData({ document_type: 'consultation_report', document_name: '', notes: '' });
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    // Build the file to send depending on input mode
    let fileToSend = uploadFile;
    if (inputMode === 'manual') {
      if (!manualContent.trim()) {
        alert('Veuillez saisir le contenu du document.');
        return;
      }
      const header = `${documentTypes[uploadData.document_type] || 'Document'}\n` +
                     `${uploadData.document_name || 'Sans titre'}\n` +
                     `Rédigé par : Dr. ${patient?.doctor_name || ''}\n` +
                     `Patient : ${patient.first_name} ${patient.last_name}\n` +
                     `Date : ${new Date().toLocaleDateString('fr-FR')}\n` +
                     `${'='.repeat(50)}\n\n`;
      const blob = new Blob([header + manualContent], { type: 'text/plain;charset=utf-8' });
      const safeName = (uploadData.document_name || 'document').replace(/[^a-zA-Z0-9-_]/g, '_');
      fileToSend = new File([blob], `${safeName}.txt`, { type: 'text/plain' });
    }

    if (!fileToSend) {
      alert('Veuillez sélectionner un fichier ou saisir le contenu.');
      return;
    }
    if (!uploadData.document_name.trim()) {
      alert('Veuillez donner un nom au document.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', fileToSend);
    formData.append('document_type', uploadData.document_type);
    formData.append('document_name', uploadData.document_name);
    formData.append('notes', uploadData.notes);

    try {
      const token = localStorage.getItem('doctor_token');
      const response = await fetch(`${API_URL}/api/doctor/patient/${patient.user_id}/documents/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        resetUploadForm();
        onRefresh();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Échec du téléversement: ${err.detail || response.status}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Erreur réseau lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FileText size={24} />
            Dossier Médical - {patient.first_name} {patient.last_name}
          </h2>
          <button onClick={onClose} className="modal-close">
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="patient-record-actions">
            <button 
              onClick={() => setShowUpload(!showUpload)} 
              className="btn-primary"
            >
              <Upload size={18} /> Ajouter un document
            </button>
          </div>

          {showUpload && (
            <form onSubmit={handleUpload} className="upload-form doctor-upload-form">
              <div className="upload-form-header">
                <FileText size={20} />
                <h4>Nouveau document médical</h4>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de document <span style={{color:'#dc2626'}}>*</span></label>
                  <select
                    value={uploadData.document_type}
                    onChange={(e) => setUploadData({...uploadData, document_type: e.target.value})}
                    data-testid="doctor-upload-type"
                  >
                    {Object.entries(documentTypes).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Nom du document <span style={{color:'#dc2626'}}>*</span></label>
                  <input
                    type="text"
                    value={uploadData.document_name}
                    onChange={(e) => setUploadData({...uploadData, document_name: e.target.value})}
                    placeholder="Ex : Ordonnance consultation du 30/07"
                    data-testid="doctor-upload-name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notes internes <span style={{color:'#94a3b8', fontWeight:400}}>(optionnel)</span></label>
                <textarea
                  value={uploadData.notes}
                  onChange={(e) => setUploadData({...uploadData, notes: e.target.value})}
                  rows="2"
                  placeholder="Notes supplémentaires visibles par le patient…"
                />
              </div>

              {/* Source picker */}
              <div className="form-group">
                <label>Source du document <span style={{color:'#dc2626'}}>*</span></label>
                <div className="upload-source-tabs" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inputMode === 'file'}
                    className={`upload-source-tab ${inputMode === 'file' ? 'active' : ''}`}
                    onClick={() => { setInputMode('file'); setUploadFile(null); }}
                    data-testid="upload-mode-file"
                  >
                    <Upload size={18} />
                    <div>
                      <strong>Fichier</strong>
                      <span>PDF, image, Word…</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inputMode === 'camera'}
                    className={`upload-source-tab ${inputMode === 'camera' ? 'active' : ''}`}
                    onClick={() => { setInputMode('camera'); setUploadFile(null); }}
                    data-testid="upload-mode-camera"
                  >
                    <Camera size={18} />
                    <div>
                      <strong>Photo</strong>
                      <span>Prendre avec l'appareil</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={inputMode === 'manual'}
                    className={`upload-source-tab ${inputMode === 'manual' ? 'active' : ''}`}
                    onClick={() => { setInputMode('manual'); setUploadFile(null); }}
                    data-testid="upload-mode-manual"
                  >
                    <PenLine size={18} />
                    <div>
                      <strong>Saisie manuelle</strong>
                      <span>Rédiger directement</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* File picker */}
              {inputMode === 'file' && (
                <div className="form-group upload-source-panel">
                  <label
                    htmlFor="doctor-upload-file"
                    className="upload-picker"
                    data-testid="doctor-choose-file-label"
                  >
                    <Upload size={22} />
                    <span>Cliquez pour choisir un fichier</span>
                    <small>PDF, image ou document Word (max ~10 Mo)</small>
                  </label>
                  <input
                    id="doctor-upload-file"
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={async (e) => setUploadFile(await compressImage(e.target.files[0]))}
                    style={{ display: 'none' }}
                    data-testid="doctor-upload-file"
                  />
                  {uploadFile && (
                    <div className="upload-picked" data-testid="doctor-selected-file">
                      <CheckCircle size={16} color="#16a34a" />
                      <span>{uploadFile.name}</span>
                      <small>({Math.round(uploadFile.size / 1024)} Ko)</small>
                    </div>
                  )}
                </div>
              )}

              {/* Camera picker */}
              {inputMode === 'camera' && (
                <div className="form-group upload-source-panel">
                  <label
                    htmlFor="doctor-upload-camera"
                    className="upload-picker"
                    data-testid="doctor-take-photo-label"
                  >
                    <Camera size={22} />
                    <span>Prendre une photo maintenant</span>
                    <small>L'appareil photo va s'ouvrir</small>
                  </label>
                  <input
                    id="doctor-upload-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={async (e) => setUploadFile(await compressImage(e.target.files[0]))}
                    style={{ display: 'none' }}
                    data-testid="doctor-upload-camera"
                  />
                  {uploadFile && (
                    <div className="upload-picked" data-testid="doctor-selected-photo">
                      <CheckCircle size={16} color="#16a34a" />
                      <span>Photo capturée</span>
                      <small>({Math.round(uploadFile.size / 1024)} Ko)</small>
                    </div>
                  )}
                </div>
              )}

              {/* Manual entry */}
              {inputMode === 'manual' && (
                <div className="form-group upload-source-panel">
                  <label htmlFor="doctor-upload-manual">Contenu du document <span style={{color:'#dc2626'}}>*</span></label>
                  <textarea
                    id="doctor-upload-manual"
                    value={manualContent}
                    onChange={(e) => setManualContent(e.target.value)}
                    rows="10"
                    placeholder={`Rédigez le contenu du document ici…\n\nExemple d'ordonnance :\n1. Paracétamol 500 mg — 1 comprimé toutes les 6 h pendant 5 jours\n2. Repos et hydratation abondante`}
                    className="upload-manual-textarea"
                    data-testid="doctor-upload-manual"
                  />
                  <small className="upload-manual-hint">
                    Le contenu sera enregistré comme fichier texte lisible par le patient dans son dossier.
                  </small>
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={resetUploadForm} className="btn-secondary">
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={uploading} data-testid="doctor-upload-submit">
                  {uploading ? 'Envoi…' : (inputMode === 'manual' ? 'Enregistrer le document' : 'Téléverser le document')}
                </button>
              </div>
            </form>
          )}

          <div className="documents-list">
            <h3>Documents ({documents.length})</h3>
            {documents.length === 0 ? (
              <p className="empty-text">Aucun document dans le dossier</p>
            ) : (
              documents.map((doc) => {
                const isUnread = (!doc.uploaded_by || doc.uploaded_by.type === 'patient') && !doc.doctor_viewed_at;
                return (
                <div
                  key={doc.document_id}
                  className={`document-item ${isUnread ? 'doc-unread' : ''}`}
                  data-testid={`doctor-doc-${doc.document_id}`}
                  style={{
                    position: 'relative',
                    ...(isUnread ? {
                      background: 'linear-gradient(90deg, #fff7ed 0%, #ffffff 70%)',
                      borderLeft: '4px solid #f59e0b',
                    } : {}),
                  }}
                >
                  <div className="doc-icon">
                    <FileText size={20} />
                  </div>
                  <div className="doc-info">
                    <strong>
                      {doc.document_name}
                      {isUnread && (
                        <span
                          data-testid={`doctor-unread-badge-${doc.document_id}`}
                          style={{
                            marginLeft: 8, background: '#f59e0b', color: '#fff',
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                            padding: '2px 6px', borderRadius: 10, verticalAlign: 'middle',
                          }}
                        >
                          NOUVEAU
                        </span>
                      )}
                    </strong>
                    <span>{documentTypes[doc.document_type] || doc.document_type}</span>
                    <span className="doc-date">{formatDate(doc.uploaded_at)}</span>
                    {doc.uploaded_by && (
                      <span className="doc-author">Par: {doc.uploaded_by.name || 'Patient'}</span>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/doctor/documents/${doc.document_id}/download`, {
                          headers: { 'Authorization': `Bearer ${localStorage.getItem('doctor_token')}` }
                        });
                        if (!res.ok) throw new Error('Accès refusé');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.target = '_blank';
                        a.rel = 'noopener';
                        a.download = doc.original_filename || doc.document_name;
                        a.click();
                        setTimeout(() => window.URL.revokeObjectURL(url), 30000);
                        // Optimistic: mark as viewed locally so the highlight disappears
                        onRefresh && onRefresh();
                      } catch (e) {
                        alert('Erreur d’ouverture du document');
                      }
                    }}
                    className="btn-icon"
                    title="Ouvrir / Télécharger"
                    data-testid={`open-doc-${doc.document_id}`}
                  >
                    <Download size={18} />
                  </button>
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export function DoctorLogin({ onLoginSuccess, onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('email'); // 'email', 'code', 'newPassword'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Retry logic for unstable networks (DRC)
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout
        
        const response = await fetch(`${API_URL}/api/doctor/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        const data = await response.json();

        if (!response.ok) {
          // Don't retry on auth errors (wrong password, etc.)
          throw new Error(data.detail || 'Erreur de connexion');
        }

        localStorage.setItem('doctor_token', data.access_token);
        localStorage.setItem('doctor_data', JSON.stringify(data.doctor));
        
        setLoading(false);
        onLoginSuccess(data.doctor);
        return; // Success - exit function
        
      } catch (err) {
        lastError = err;
        
        // Don't retry on authentication errors
        if (err.message && !err.message.includes('fetch') && err.name !== 'AbortError') {
          break;
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          console.log(`[Login] Attempt ${attempt} failed, retrying in ${attempt}s...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        }
      }
    }
    
    // All retries failed
    if (lastError) {
      if (lastError.name === 'AbortError') {
        setError('Connexion trop lente. Vérifiez votre connexion internet et réessayez.');
      } else if (lastError.message === 'Failed to fetch' || (lastError.message || '').includes('NetworkError')) {
        setError('Impossible de contacter le serveur après plusieurs tentatives. Vérifiez votre connexion internet.');
      } else {
        setError(lastError.message || 'Erreur de connexion');
      }
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetMessage('');
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      
      const data = await response.json();
      setResetMessage(data.message);
      if (response.ok) {
        setResetStep('code');
      }
    } catch (err) {
      setResetMessage('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setResetMessage('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      setResetMessage('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    setResetMessage('');
    
    try {
      const response = await fetch(`${API_URL}/api/doctor/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: resetEmail, 
          code: resetCode, 
          new_password: newPassword 
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        alert('Mot de passe réinitialisé avec succès !');
        setShowForgotPassword(false);
        setResetStep('email');
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setResetMessage(data.detail || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setResetMessage('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="doctor-login-page">
        <div className="login-card">
          <div className="login-header">
            <Stethoscope size={40} />
            <h1>Récupération</h1>
            <p>Réinitialiser votre mot de passe</p>
          </div>

          {resetStep === 'email' && (
            <form onSubmit={handleForgotPassword} className="login-form">
              {resetMessage && <div className="info-message">{resetMessage}</div>}
              
              <div className="form-group">
                <label>Votre Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="medecin@clinique.cd"
                  required
                />
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Envoi...' : 'Envoyer le code'}
              </button>
              
              <button type="button" onClick={() => setShowForgotPassword(false)} className="btn-back">
                ← Retour à la connexion
              </button>
            </form>
          )}

          {resetStep === 'code' && (
            <form onSubmit={(e) => { e.preventDefault(); setResetStep('newPassword'); }} className="login-form">
              {resetMessage && <div className="info-message">{resetMessage}</div>}
              
              <div className="form-group">
                <label>Code de récupération</label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="Code à 6 chiffres"
                  maxLength={6}
                  required
                />
              </div>
              
              <button type="submit" className="btn-primary">
                Vérifier le code
              </button>
              
              <button type="button" onClick={() => setResetStep('email')} className="btn-back">
                ← Renvoyer le code
              </button>
            </form>
          )}

          {resetStep === 'newPassword' && (
            <form onSubmit={handleResetPassword} className="login-form">
              {resetMessage && <div className="error-message">{resetMessage}</div>}
              
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Confirmer le mot de passe</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
              </div>
              
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? 'Réinitialisation...' : 'Réinitialiser'}
              </button>
              
              <button type="button" onClick={() => setResetStep('code')} className="btn-back">
                ← Retour
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-login-page">
      <div className="login-card">
        <div className="login-header">
          <Stethoscope size={40} />
          <h1>Portail Médecin</h1>
          <p>Centre Médical La Trinité</p>
        </div>

        <form onSubmit={handleLogin} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="medecin@clinique.cd"
              required
              data-testid="doctor-login-email"
            />
          </div>
          
          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="doctor-login-password"
            />
          </div>
          
          <button type="submit" className="btn-primary" disabled={loading} data-testid="doctor-login-submit">
            {loading ? 'Connexion...' : 'Se Connecter'}
          </button>
          
          <button 
            type="button" 
            onClick={() => setShowForgotPassword(true)} 
            className="btn-link"
          >
            Mot de passe oublié ?
          </button>
          
          {onBack && (
            <button type="button" onClick={onBack} className="btn-back">
              ← Retour à l'accueil
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export default DoctorPortal;
